// Prescription.jsx (FULL COPY-PASTE UPDATED)
// ✅ Default shows TODAY created prescriptions only
// ✅ Date preset filter: Today / Yesterday / Last 7 Days / All / Custom
// ✅ Custom uses fromDate & toDate
// ✅ Works with backend once you add fromDate/toDate support (backend patch below)
// ✅ Search + Status + Pagination kept same

import React, { useState, useEffect, useMemo } from "react";
import Header from "../../components/ui/Header";
import Sidebar from "../../components/ui/Sidebar";
import Breadcrumb from "../../components/ui/Breadcrumb";
import Button from "../../components/ui/Button";
import API_BASE_URL from "../../services/api";
import axiosInstance from "../../utils/axiosInstance";

const DATE_PRESETS = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "last7", label: "Last 7 Days" },
  { value: "all", label: "All" },
  { value: "custom", label: "Custom Range" },
];

const toYMD = (d) => {
  const dt = new Date(d);
  const yyyy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const dd = String(dt.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const computeRangeFromPreset = (preset) => {
  const now = new Date();
  const today = toYMD(now);

  if (preset === "today") return { fromDate: today, toDate: today };

  if (preset === "yesterday") {
    const y = new Date(now);
    y.setDate(y.getDate() - 1);
    const yd = toYMD(y);
    return { fromDate: yd, toDate: yd };
  }

  if (preset === "last7") {
    const start = new Date(now);
    start.setDate(start.getDate() - 6);
    return { fromDate: toYMD(start), toDate: today };
  }

  return { fromDate: "", toDate: "" }; // all
};

const Prescription = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const [prescriptions, setPrescriptions] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({ total: 0, currentPage: 1, totalPages: 1, limit: 10 });
  const [loading, setLoading] = useState(false);

  // ✅ Date filter (default: today)
  const [datePreset, setDatePreset] = useState("today");
  const [fromDate, setFromDate] = useState(() => computeRangeFromPreset("today").fromDate);
  const [toDate, setToDate] = useState(() => computeRangeFromPreset("today").toDate);

  // Modal State
  const [showModal, setShowModal] = useState(false);
  const [selectedPrescription, setSelectedPrescription] = useState(null);
  const [remarks, setRemarks] = useState("");

  const showFromToInputs = datePreset === "custom";

  const queryParams = useMemo(() => {
    const range =
      datePreset === "custom" ? { fromDate, toDate } : computeRangeFromPreset(datePreset);

    return {
      page,
      limit: 10,
      ...(search.trim() ? { search: search.trim() } : {}),
      ...(status ? { status } : {}),
      ...(range.fromDate ? { fromDate: range.fromDate } : {}),
      ...(range.toDate ? { toDate: range.toDate } : {}),
    };
  }, [page, search, status, datePreset, fromDate, toDate]);

  const fetchPrescriptions = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get(`${API_BASE_URL}/api/prescription`, {
        params: queryParams,
      });

      if (res.data.success) {
        setPrescriptions(res.data.data || []);
        setMeta(res.data.meta || { total: 0, currentPage: 1, totalPages: 1, limit: 10 });
      }
    } catch (err) {
      console.error("Error fetching prescriptions:", err);
      setPrescriptions([]);
      setMeta({ total: 0, currentPage: 1, totalPages: 1, limit: 10 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPrescriptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();

    if (datePreset === "custom") {
      if (!fromDate || !toDate) return alert("Please select both From and To date.");
      if (new Date(fromDate) > new Date(toDate)) return alert("From date cannot be after To date.");
    }

    setPage(1);
    fetchPrescriptions();
  };

  const handleReset = () => {
    setSearch("");
    setStatus("");
    setPage(1);

    setDatePreset("today");
    const r = computeRangeFromPreset("today");
    setFromDate(r.fromDate);
    setToDate(r.toDate);

    fetchPrescriptions();
  };

  // ✅ Handle approve with remarks
  const handleApprove = async () => {
    try {
      if (!remarks.trim()) {
        alert("Please enter remarks before approving.");
        return;
      }

      const res = await axiosInstance.put(
        `${API_BASE_URL}/api/prescription/review/${selectedPrescription.id}`,
        { status: "APPROVED", remarks, reviewedById: 1 }
      );

      if (res.data?.success || res.status === 200) {
        setShowModal(false);
        setRemarks("");
        setSelectedPrescription(null);
        fetchPrescriptions();
      } else {
        alert(res.data?.message || "Failed to approve prescription.");
      }
    } catch (err) {
      console.error("Error approving prescription:", err);
      alert("Failed to approve prescription.");
    }
  };

  const handleMenuToggle = () => setSidebarOpen(!sidebarOpen);
  const handleSidebarClose = () => setSidebarOpen(false);

  const getStatusBadge = (st) => {
    switch (st) {
      case "APPROVED":
        return "bg-green-50 text-green-700 border border-green-200";
      case "PENDING_REVIEW":
        return "bg-yellow-50 text-yellow-700 border border-yellow-200";
      case "REJECTED":
        return "bg-red-50 text-red-700 border border-red-200";
      default:
        return "bg-gray-50 text-gray-700 border border-gray-200";
    }
  };

  const getStatusIcon = (st) => {
    switch (st) {
      case "APPROVED":
        return "✓";
      case "PENDING_REVIEW":
        return "⏳";
      case "REJECTED":
        return "✕";
      default:
        return "●";
    }
  };

  const truncateText = (text, maxLength = 50) => {
    if (!text) return "N/A";
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header onMenuToggle={handleMenuToggle} isMenuOpen={sidebarOpen} />
   <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        isCollapsed={sidebarCollapsed}
        onCollapseToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
      />

      <main
        className={`pt-16 transition-all duration-300 ${
          sidebarCollapsed ? "lg:ml-16" : "lg:ml-60"
        }`}
      >
        <div className="p-4">
          <Breadcrumb />

          {/* Header */}
          <div className="mb-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 mb-4">
              <div>
                <h1 className="text-xl font-bold text-gray-900">Prescription Management</h1>
                <p className="text-xs text-gray-600 mt-1">
                  Default shows Today uploaded prescriptions only
                </p>
              </div>

              <div className="flex items-center gap-3 text-xs">
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className="text-gray-600">Approved</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                  <span className="text-gray-600">Pending</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                  <span className="text-gray-600">Rejected</span>
                </div>
              </div>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
              <form onSubmit={handleSearchSubmit} className="flex flex-col gap-3">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
                  {/* Search */}
                  <div className="md:col-span-5">
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Search Prescriptions
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                        <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                      </div>
                      <input
                        type="text"
                        placeholder="Search by patient name or contact..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="block w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md bg-white placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-xs transition-colors"
                      />
                    </div>
                  </div>

                  {/* Status */}
                  <div className="md:col-span-2">
                    <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
                    <select
                      value={status}
                      onChange={(e) => {
                        setStatus(e.target.value);
                        setPage(1);
                      }}
                      className="w-full px-2 py-2 border border-gray-300 rounded-md bg-white text-gray-900 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-xs transition-colors"
                    >
                      <option value="">All Status</option>
                      <option value="PENDING_REVIEW">Pending Review</option>
                      <option value="APPROVED">Approved</option>
                      <option value="REJECTED">Rejected</option>
                    </select>
                  </div>

                  {/* Date preset */}
                  <div className="md:col-span-2">
                    <label className="block text-xs font-medium text-gray-700 mb-1">Date</label>
                    <select
                      value={datePreset}
                      onChange={(e) => {
                        const v = e.target.value;
                        setDatePreset(v);
                        setPage(1);
                        if (v !== "custom") {
                          const r = computeRangeFromPreset(v);
                          setFromDate(r.fromDate);
                          setToDate(r.toDate);
                        }
                      }}
                      className="w-full px-2 py-2 border border-gray-300 rounded-md bg-white text-gray-900 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-xs transition-colors"
                    >
                      {DATE_PRESETS.map((p) => (
                        <option key={p.value} value={p.value}>
                          {p.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* From */}
                  <div className={`md:col-span-1 ${showFromToInputs ? "" : "opacity-60 pointer-events-none"}`}>
                    <label className="block text-xs font-medium text-gray-700 mb-1">From</label>
                    <input
                      type="date"
                      value={fromDate}
                      onChange={(e) => setFromDate(e.target.value)}
                      className="w-full px-2 py-2 border border-gray-300 rounded-md bg-white text-gray-900 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-xs transition-colors"
                    />
                  </div>

                  {/* To */}
                  <div className={`md:col-span-1 ${showFromToInputs ? "" : "opacity-60 pointer-events-none"}`}>
                    <label className="block text-xs font-medium text-gray-700 mb-1">To</label>
                    <input
                      type="date"
                      value={toDate}
                      onChange={(e) => setToDate(e.target.value)}
                      className="w-full px-2 py-2 border border-gray-300 rounded-md bg-white text-gray-900 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-xs transition-colors"
                    />
                  </div>

                  {/* Actions */}
                  <div className="md:col-span-1 flex items-end gap-2">
                    <Button
                      type="submit"
                      variant="solid"
                      size="sm"
                      iconName="Search"
                      className="bg-blue-600 hover:bg-blue-700 text-white h-9 px-4 text-xs w-full"
                    >
                      Search
                    </Button>
                  </div>
                </div>

                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={handleReset}
                    className="text-xs text-gray-500 hover:text-gray-700 px-3 py-1 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Reset Filters
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* Table */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">Prescription Records</h3>
                  <p className="text-xs text-gray-600 mt-0.5">{meta.total || 0} records found</p>
                </div>
                {meta.totalPages > 1 && (
                  <div className="flex items-center gap-2 mt-1 sm:mt-0">
                    <span className="text-xs text-gray-600">
                      Page {meta.currentPage} of {meta.totalPages}
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700 uppercase tracking-wider w-12">
                      SL No
                    </th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700 uppercase tracking-wider">
                      Patient Information
                    </th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700 uppercase tracking-wider">
                      Contact
                    </th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700 uppercase tracking-wider">
                      Reviewer
                    </th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700 uppercase tracking-wider">
                      File Type
                    </th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700 uppercase tracking-wider">
                      Remarks
                    </th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700 uppercase tracking-wider">
                      Upload Date
                    </th>
                    <th className="px-3 py-2 text-right font-semibold text-gray-700 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>

                <tbody className="bg-white divide-y divide-gray-200">
                  {loading ? (
                    <tr>
                      <td colSpan="9" className="px-4 py-8 text-center">
                        <div className="flex justify-center items-center">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                        </div>
                        <p className="mt-2 text-xs text-gray-500">Loading prescriptions...</p>
                      </td>
                    </tr>
                  ) : prescriptions?.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="px-4 py-8 text-center">
                        <div className="flex flex-col items-center justify-center">
                          <svg className="w-10 h-10 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <p className="text-gray-500 font-medium text-xs">No prescriptions found</p>
                          <p className="text-gray-400 text-xs mt-0.5">
                            {search || status ? "Try adjusting your search or filter" : "No prescriptions uploaded"}
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    prescriptions.map((prescription, index) => (
                      <tr key={prescription.id} className="hover:bg-gray-50 transition-colors duration-150">
                        <td className="px-3 py-2 text-gray-500 font-medium">
                          {(meta?.limit || 10) * (page - 1) + (index + 1)}
                        </td>

                        <td className="px-3 py-2">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-8 w-8 bg-blue-100 rounded-md flex items-center justify-center">
                              <svg className="h-4 w-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                              </svg>
                            </div>
                            <div className="ml-2">
                              <div className="text-xs font-medium text-gray-900">
                                {prescription?.patient?.fullName || "Unknown Patient"}
                              </div>
                              <div className="text-xs text-gray-500">
                                {prescription?.patient?.email || "No email"}
                              </div>
                            </div>
                          </div>
                        </td>

                        <td className="px-3 py-2">
                          <div className="text-xs text-gray-900">
                            {prescription?.patient?.contactNo || "N/A"}
                          </div>
                        </td>

                        <td className="px-3 py-2">
                          <div className="text-xs text-gray-900">
                            {prescription?.reviewedBy?.name ? (
                              <span className="inline-flex items-center">
                                <span className="w-1.5 h-1.5 bg-green-400 rounded-full mr-1"></span>
                                {prescription.reviewedBy.name}
                              </span>
                            ) : (
                              <span className="text-gray-400">Not reviewed</span>
                            )}
                          </div>
                        </td>

                        <td className="px-3 py-2">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {prescription?.fileType || "Unknown"}
                          </span>
                        </td>

                        <td className="px-3 py-2">
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(
                              prescription.status
                            )}`}
                          >
                            <span className="mr-1">{getStatusIcon(prescription.status)}</span>
                            {String(prescription.status || "").replaceAll("_", " ")}
                          </span>
                        </td>

                        <td className="px-3 py-2">
                          <div className="text-xs text-gray-600 max-w-[120px]">
                            {truncateText(prescription.remarks, 30)}
                          </div>
                        </td>

                        <td className="px-3 py-2">
                          <div className="text-xs text-gray-900">
                            {prescription.uploadedAt ? new Date(prescription.uploadedAt).toLocaleDateString() : "-"}
                          </div>
                          <div className="text-xs text-gray-500">
                            {prescription.uploadedAt ? new Date(prescription.uploadedAt).toLocaleTimeString() : "-"}
                          </div>
                        </td>

                        <td className="px-3 py-2">
                          <div className="flex justify-end space-x-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              iconName="Eye"
                              className="text-gray-600 hover:text-gray-800 border border-gray-300 hover:border-gray-400 text-xs px-2 py-1"
                              onClick={() => window.open(prescription.fileUrl, "_blank")}
                            >
                              View
                            </Button>

                            {prescription.status === "PENDING_REVIEW" && (
                              <Button
                                variant="solid"
                                size="sm"
                                iconName="Edit3"
                                className="bg-blue-600 hover:bg-blue-700 text-white text-xs px-2 py-1"
                                onClick={() => {
                                  setSelectedPrescription(prescription);
                                  setShowModal(true);
                                }}
                              >
                                Review
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {meta?.totalPages > 1 && (
              <div className="px-4 py-3 bg-gray-50 border-t border-gray-200">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="text-xs text-gray-700">
                    Showing <span className="font-medium">{(meta.currentPage - 1) * meta.limit + 1}</span> to{" "}
                    <span className="font-medium">{Math.min(meta.currentPage * meta.limit, meta.total)}</span> of{" "}
                    <span className="font-medium">{meta.total}</span> results
                  </div>

                  <div className="flex items-center space-x-1">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page === 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      className="flex items-center gap-1 text-xs px-2 py-1"
                    >
                      Prev
                    </Button>

                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page === meta.totalPages}
                      onClick={() => setPage((p) => Math.min(meta.totalPages, p + 1))}
                      className="flex items-center gap-1 text-xs px-2 py-1"
                    >
                      Next
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="px-4 py-3 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-base font-semibold text-gray-900">Review Prescription</h3>
                <button
                  onClick={() => {
                    setShowModal(false);
                    setRemarks("");
                    setSelectedPrescription(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  ✕
                </button>
              </div>
              <p className="text-xs text-gray-600 mt-0.5">
                Add remarks for {selectedPrescription?.patient?.fullName || "the patient"}
              </p>
            </div>

            <div className="px-4 py-3">
              <label className="block text-xs font-medium text-gray-700 mb-1">Remarks</label>
              <textarea
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                className="w-full px-2 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 resize-none transition-colors text-xs"
                rows="3"
                placeholder="Enter your remarks here..."
              />
            </div>

            <div className="px-4 py-3 bg-gray-50 rounded-b-lg flex justify-end space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setShowModal(false);
                  setRemarks("");
                  setSelectedPrescription(null);
                }}
                className="border-gray-300 text-gray-700 hover:bg-gray-50 text-xs px-3 py-1.5"
              >
                Cancel
              </Button>
              <Button
                variant="solid"
                size="sm"
                className="bg-green-600 hover:bg-green-700 text-white text-xs px-3 py-1.5"
                onClick={handleApprove}
                disabled={!remarks.trim()}
              >
                Approve
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Prescription;
